/**
 * TradingView-style multi-chart layout presets.
 * Each layout defines a CSS grid template and pane count.
 * User choice is persisted via boardSettings.layoutId.
 */

export const CHART_LAYOUTS = [
  {
    id: '1',
    label: '1',
    title: 'Single',
    panes: 1,
    // CSS grid-template-areas rows
    areas: ['a'],
    cols: '1fr',
    rows: '1fr',
  },
  {
    id: '2h',
    label: '2',
    title: '2 side by side',
    panes: 2,
    areas: ['a b'],
    cols: '1fr 1fr',
    rows: '1fr',
  },
  {
    id: '2v',
    label: '2↕',
    title: '2 stacked',
    panes: 2,
    areas: ['a', 'b'],
    cols: '1fr',
    rows: '1fr 1fr',
  },
  {
    id: '3',
    label: '3',
    title: '1 large + 2 stacked',
    panes: 3,
    areas: ['a b', 'a c'],
    cols: '1.4fr 1fr',
    rows: '1fr 1fr',
  },
  {
    id: '3h',
    label: '3↔',
    title: '3 columns',
    panes: 3,
    areas: ['a b c'],
    cols: '1fr 1fr 1fr',
    rows: '1fr',
  },
  {
    id: '3v',
    label: '3↕',
    title: '3 stacked',
    panes: 3,
    areas: ['a', 'b', 'c'],
    cols: '1fr',
    rows: '1fr 1fr 1fr',
  },
  {
    id: '4',
    label: '4',
    title: '2×2 grid',
    panes: 4,
    areas: ['a b', 'c d'],
    cols: '1fr 1fr',
    rows: '1fr 1fr',
  },
  {
    id: '4h',
    label: '4↔',
    title: '1 top + 3 bottom',
    panes: 4,
    areas: ['a a a', 'b c d'],
    cols: '1fr 1fr 1fr',
    rows: '1.2fr 1fr',
  },
  {
    id: '6',
    label: '6',
    title: '2×3 grid',
    panes: 6,
    areas: ['a b c', 'd e f'],
    cols: '1fr 1fr 1fr',
    rows: '1fr 1fr',
  },
];

export const DEFAULT_LAYOUT_ID = '3';

export function getLayoutById(id) {
  return CHART_LAYOUTS.find((l) => l.id === id) || CHART_LAYOUTS.find((l) => l.id === DEFAULT_LAYOUT_ID);
}

/** Map board symbols onto layout panes (cycles if more panes than symbols). */
export function assignSymbolsToPanes(symbols, layoutId) {
  const layout = getLayoutById(layoutId);
  const list = Array.isArray(symbols) ? symbols : [];
  if (!list.length) return [];
  const panes = [];
  for (let i = 0; i < layout.panes; i++) {
    const sym = list[i % list.length];
    const area = String.fromCharCode(97 + i); // a, b, c...
    panes.push({
      paneIndex: i,
      area,
      symbol: sym,
      // Unique key when same symbol appears twice
      key: `${sym.id}-p${i}`,
    });
  }
  return panes;
}

export function layoutGridStyle(layoutId) {
  const layout = getLayoutById(layoutId);
  return {
    display: 'grid',
    gridTemplateColumns: layout.cols,
    gridTemplateRows: layout.rows,
    gridTemplateAreas: layout.areas.map((r) => `"${r}"`).join(' '),
    gap: '2px',
    width: '100%',
    height: '100%',
    minHeight: 0,
  };
}

/** Bottom date-range bar (TradingView-style). */
export const TV_DATE_RANGES = [
  { id: '1D', label: '1D', eodPeriod: '5D', intradayDays: 1 },
  { id: '5D', label: '5D', eodPeriod: '5D', intradayDays: 5 },
  { id: '1M', label: '1M', eodPeriod: '1M', intradayDays: 22 },
  { id: '3M', label: '3M', eodPeriod: '3M', intradayDays: 30 },
  { id: '6M', label: '6M', eodPeriod: '6M', intradayDays: 30 },
  { id: 'YTD', label: 'YTD', eodPeriod: '1Y', intradayDays: 30 },
  { id: '1Y', label: '1Y', eodPeriod: '1Y', intradayDays: 30 },
  { id: '5Y', label: '5Y', eodPeriod: '5Y', intradayDays: 30 },
  { id: 'ALL', label: 'All', eodPeriod: 'ALL', intradayDays: 30 },
];

export function getDateRangeById(id) {
  return TV_DATE_RANGES.find((r) => r.id === id) || TV_DATE_RANGES[2];
}
